import { useEffect, useState } from "react";
import ReactPaginate from "react-paginate";
import { useSelector } from "react-redux";

import {
  ArrowDownUp,
  ArrowRight,
  ArrowUpDown,
  Map,
  SquareCheck,
  SquareX,
  X,
} from "lucide-react";
import ParagraphTag from "../../reusebale/Paragraph";
import ImageTag from "../../reusebale/imageTag";
import NoData from "../../reusebale/noData";
import LeaseTableLoader from "../../skeleton-loader/lease-table.jsx";
import MapSection from "./map";

import BaseUrl from "../../../constants/BaseUrl.js";
import db from "../../../db/index.json";

const PropertyTable = ({ onViewDetails, searchTerm }) => {
  const { allProperties } = useSelector((state) => state.property);
  const [AllProperty, setAllProperty] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [Loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalData, setTotalData] = useState(1);
  const itemsPerPage = 10;

  const { loginSuccess } = useSelector((state) => state.auth);

  useEffect(() => {
    GetAllProperty();
  }, [currentPage, loginSuccess?.token]);

  const GetAllProperty = () => {
    setLoading(true);
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("Authorization", `Bearer ${loginSuccess?.token}`);
    const requestOptions = {
      method: "GET",
      headers: myHeaders,
      redirect: "follow",
    };

    fetch(
      `${BaseUrl}api/property/getProperties?page=${currentPage}&limit=${itemsPerPage}`,
      requestOptions
    )
      .then((response) => response.json())
      .then((result) => {
        if (result.success) {
          setLoading(false);
          setAllProperty(result.data);
          setTotalData(result.total);
        } else {
          setLoading(false);
        }
      })
      .catch((error) => console.error(error));
  };

  const { properties } = db;
  const [showMap, setShowMap] = useState(false);

  useEffect(() => {
    if (Array.isArray(allProperties)) {
      setAllProperty(allProperties);
    }
  }, [allProperties]);

  const toggleMap = () => {
    setShowMap(!showMap); // Map visibility toggle
  };

  const handlePageClick = (selectedPage) => {
    setCurrentPage(selectedPage.selected + 1);
  };

  const [sortConfig, setSortConfig] = useState({
    key: "name", // Default sort key
    direction: "asc", // Default sort direction
  });

  // Filter properties based on search term
  useEffect(() => {
    if (!searchTerm || searchTerm.trim() === "") {
      setFilteredProperties(AllProperty);
    } else {
      const filtered = AllProperty.filter((property) => {
        const propertyName = property?.propertyName?.toLowerCase() || "";
        const propertyAddress = property?.address?.toLowerCase() || "";
        const state = property?.state?.toLowerCase() || "";
        const category = property?.category?.toLowerCase() || "";
        const type = property?.type?.toLowerCase() || "";

        return (
          propertyName.includes(searchTerm.toLowerCase()) ||
          propertyAddress.includes(searchTerm.toLowerCase()) ||
          state.includes(searchTerm.toLowerCase()) ||
          category.includes(searchTerm.toLowerCase()) ||
          type.includes(searchTerm.toLowerCase())
        );
      });
      setFilteredProperties(filtered);
    }
  }, [AllProperty, searchTerm]);

  useEffect(() => {
    setFilteredProperties(AllProperty);
  }, [AllProperty]);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });

    const sortedData = [...filteredProperties].sort((a, b) => {
      if (typeof a[key] === "string" && typeof b[key] === "string") {
        return direction === "asc"
          ? a[key].localeCompare(b[key])
          : b[key].localeCompare(a[key]);
      } else {
        return direction === "asc" ? a[key] - b[key] : b[key] - a[key];
      }
    });

    setFilteredProperties(sortedData); // apply sort to filteredProperties
  };

  return (
    <div className="flex justify-between h-full pt-5">
      <div className={`${showMap ? "w-[64%]" : "w-full"} h-full`}>
        <div className={`bg-white h-[90%]`}>
          <div className="w-[95%] mx-auto">
            <div className="flex justify-between py-5">
              <div className={`${showMap ? "w-[50%]" : "w-[70%]"}`}>
                <ParagraphTag
                  content={properties.heading}
                  classes="text-darkColor font-popinsSemiBold text-base"
                />
              </div>

              <div
                className={`flex justify-end ${
                  showMap ? "w-[50%]" : "w-[30%]"
                }`}
              >
                <button
                  className={`${
                    showMap ? "border-themeColor" : "border-[#6F6F6F]/70"
                  } border w-[48%] rounded-md flex items-center justify-center py-2`}
                  onClick={toggleMap}
                >
                  {showMap ? (
                    <X color="#FF814E" size={15} />
                  ) : (
                    <Map size={15} />
                  )}

                  <ParagraphTag
                    content={showMap ? properties.closeMap : properties.map}
                    classes={`${
                      showMap ? "text-themeColor" : "text-[#6F6F6F]"
                    } font-popinsSemiBold text-xs pl-2`}
                  />
                </button>
              </div>
            </div>
            <div className="overflow-y-scroll w-full h-[500px] panelScroll mt-1">
              <table className="w-full ">
                <thead className="sticky top-0 bg-white z-10">
                  <tr className="border-b border-black/20">
                    <th className="w-[23%] text-left pb-2">
                      <span className="flex">
                        <ParagraphTag
                          content={properties.heading}
                          classes="text-darkColor/40 font-popinsSemiBold text-xs pr-1"
                        />
                        {sortConfig.key === "_id" ? (
                          sortConfig.direction === "asc" ? (
                            <ArrowUpDown
                              className="cursor-pointer"
                              size={13}
                              color="#6F6F6F"
                              onClick={() => requestSort("_id")}
                            />
                          ) : (
                            <ArrowDownUp
                              className="cursor-pointer"
                              size={13}
                              color="#6F6F6F"
                              onClick={() => requestSort("_id")}
                            />
                          )
                        ) : (
                          <ArrowUpDown
                            className="cursor-pointer"
                            size={13}
                            color="#6F6F6F"
                            onClick={() => requestSort("_id")}
                          />
                        )}
                      </span>
                    </th>
                    <th className="w-[23%] text-left pb-2">
                      <span className="flex">
                        <ParagraphTag
                          content="Landlord"
                          classes="text-darkColor/40 font-popinsSemiBold text-xs pr-1"
                        />
                        {sortConfig.key === "_id" ? (
                          sortConfig.direction === "asc" ? (
                            <ArrowUpDown
                              className="cursor-pointer"
                              size={13}
                              color="#6F6F6F"
                              onClick={() => requestSort("_id")}
                            />
                          ) : (
                            <ArrowDownUp
                              className="cursor-pointer"
                              size={13}
                              color="#6F6F6F"
                              onClick={() => requestSort("_id")}
                            />
                          )
                        ) : (
                          <ArrowUpDown
                            className="cursor-pointer"
                            size={13}
                            color="#6F6F6F"
                            onClick={() => requestSort("_id")}
                          />
                        )}
                      </span>
                    </th>
                    <th className="w-[6%] pb-2">
                      <span className="flex justify-center">
                        <ParagraphTag
                          content={properties.status}
                          classes="text-darkColor/40 font-popinsSemiBold text-xs pr-1"
                        />
                        {sortConfig.key === "status" ? (
                          sortConfig.direction === "asc" ? (
                            <ArrowUpDown
                              className="cursor-pointer"
                              size={13}
                              color="#6F6F6F"
                              onClick={() => requestSort("status")}
                            />
                          ) : (
                            <ArrowDownUp
                              className="cursor-pointer"
                              size={13}
                              color="#6F6F6F"
                              onClick={() => requestSort("status")}
                            />
                          )
                        ) : (
                          <ArrowUpDown
                            className="cursor-pointer"
                            size={13}
                            color="#6F6F6F"
                            onClick={() => requestSort("status")}
                          />
                        )}
                      </span>
                    </th>
                    {!showMap && (
                      <>
                        <th className="w-[8%] pb-2">
                          <span className="flex justify-center">
                            <ParagraphTag
                              content={properties.units}
                              classes="text-darkColor/40 font-popinsSemiBold text-xs pr-1"
                            />
                            {sortConfig.key === "unitCount" ? (
                              sortConfig.direction === "asc" ? (
                                <ArrowUpDown
                                  className="cursor-pointer"
                                  size={13}
                                  color="#6F6F6F"
                                  onClick={() => requestSort("unitCount")}
                                />
                              ) : (
                                <ArrowDownUp
                                  className="cursor-pointer"
                                  size={13}
                                  color="#6F6F6F"
                                  onClick={() => requestSort("unitCount")}
                                />
                              )
                            ) : (
                              <ArrowUpDown
                                className="cursor-pointer"
                                size={13}
                                color="#6F6F6F"
                                onClick={() => requestSort("unitCount")}
                              />
                            )}
                          </span>
                        </th>
                        <th className="w-[8%] pb-2">
                          <span className="flex justify-center">
                            <ParagraphTag
                              content={properties.leased}
                              classes="text-darkColor/40 font-popinsSemiBold text-xs pl-2"
                            />
                            {sortConfig.key === "leaseUnitCount" ? (
                              sortConfig.direction === "asc" ? (
                                <ArrowUpDown
                                  className="cursor-pointer"
                                  size={13}
                                  color="#6F6F6F"
                                  onClick={() => requestSort("leaseUnitCount")}
                                />
                              ) : (
                                <ArrowDownUp
                                  className="cursor-pointer"
                                  size={13}
                                  color="#6F6F6F"
                                  onClick={() => requestSort("leaseUnitCount")}
                                />
                              )
                            ) : (
                              <ArrowUpDown
                                className="cursor-pointer"
                                size={13}
                                color="#6F6F6F"
                                onClick={() => requestSort("leaseUnitCount")}
                              />
                            )}
                          </span>
                        </th>
                        <th className="w-[9%] pb-2">
                          <span className="flex justify-center">
                            <ParagraphTag
                              content={"State"}
                              classes="text-darkColor/40 font-popinsSemiBold text-xs pr-1"
                            />
                            {sortConfig.key === "state" ? (
                              sortConfig.direction === "asc" ? (
                                <ArrowUpDown
                                  className="cursor-pointer"
                                  size={13}
                                  color="#6F6F6F"
                                  onClick={() => requestSort("state")}
                                />
                              ) : (
                                <ArrowDownUp
                                  className="cursor-pointer"
                                  size={13}
                                  color="#6F6F6F"
                                  onClick={() => requestSort("state")}
                                />
                              )
                            ) : (
                              <ArrowUpDown
                                className="cursor-pointer"
                                size={13}
                                color="#6F6F6F"
                                onClick={() => requestSort("state")}
                              />
                            )}
                          </span>
                        </th>
                      </>
                    )}
                    <th className="w-[15%] pb-2">
                      <span className="flex justify-center">
                        <ParagraphTag
                          content={"Category (Type)"}
                          classes="text-darkColor/40 font-popinsSemiBold text-xs pr-1"
                        />
                        {sortConfig.key === "category" ? (
                          sortConfig.direction === "asc" ? (
                            <ArrowUpDown
                              className="cursor-pointer"
                              size={13}
                              color="#6F6F6F"
                              onClick={() => requestSort("category")}
                            />
                          ) : (
                            <ArrowDownUp
                              className="cursor-pointer"
                              size={13}
                              color="#6F6F6F"
                              onClick={() => requestSort("category")}
                            />
                          )
                        ) : (
                          <ArrowUpDown
                            className="cursor-pointer"
                            size={13}
                            color="#6F6F6F"
                            onClick={() => requestSort("category")}
                          />
                        )}
                      </span>
                    </th>
                    <th className="w-[8%] pb-2">
                      <span className="flex justify-center">
                        <ParagraphTag
                          content={"Type"}
                          classes="text-darkColor/40 font-popinsSemiBold text-xs pr-1"
                        />
                        {sortConfig.key === "type" ? (
                          sortConfig.direction === "asc" ? (
                            <ArrowUpDown
                              className="cursor-pointer"
                              size={13}
                              color="#6F6F6F"
                              onClick={() => requestSort("type")}
                            />
                          ) : (
                            <ArrowDownUp
                              className="cursor-pointer"
                              size={13}
                              color="#6F6F6F"
                              onClick={() => requestSort("type")}
                            />
                          )
                        ) : (
                          <ArrowUpDown
                            className="cursor-pointer"
                            size={13}
                            color="#6F6F6F"
                            onClick={() => requestSort("type")}
                          />
                        )}
                      </span>
                    </th>
                    <th className="w-[12%] text-right pr-4 pb-2">
                      <ParagraphTag
                        content={"Quick Actions"}
                        classes="text-darkColor/40 font-popinsSemiBold text-xs"
                      />
                    </th>
                  </tr>
                </thead>
                <tbody className="">
                  {filteredProperties?.map((item) => {
                    return (
                      <tr
                        key={item.id}
                        className="hover:bg-gray-100 rounded-md cursor-pointe"
                      >
                        <td className="py-2">
                          <div className="flex items-center">
                            <div className="">
                              <ImageTag
                                path={item?.thumbnailImage?.url}
                                classes="w-[50px] rounded-full h-[50px] object-cover"
                                altText="logo"
                              />
                            </div>
                            <div
                              className=" cursor-pointer pl-3"
                              onClick={() => {
                                onViewDetails(item._id);
                              }}
                            >
                              <ParagraphTag
                                content={`PROP-${item?._id?.slice(0, 10)}`}
                                classes="text-darkColor/50 font-popinsMedium text-xs"
                              />
                              <ParagraphTag
                                content={item.propertyName}
                                classes="text-darkColor font-popinsSemiBold text-sm"
                              />
                            </div>
                          </div>
                        </td>
                        <td className="py-2">
                          <div className="flex items-center justify-between">
                            <div className="w-[25%] rounded-full">
                              <ImageTag
                                path={
                                  item?.landlordCompleteProfile
                                    ?.personalInformation?.profileImage?.url ||
                                  "/assets/daashboard/landlord-color.png"
                                }
                                classes="w-[45px] h-[45px] rounded-full"
                                altText="logo"
                              />
                            </div>
                            <div className="w-[75%] cursor-pointer">
                              <ParagraphTag
                                content={`LID-${item?.landlordId?.slice(
                                  0,
                                  10
                                )}`}
                                classes="text-darkColor/50 font-popinsMedium text-xs"
                              />
                              <ParagraphTag
                                content={`${item?.landlordCompleteProfile?.personalInformation?.firstName} ${item?.landlordCompleteProfile?.personalInformation?.lastName}`}
                                classes="text-darkColor font-popinsSemiBold text-sm"
                              />
                            </div>
                          </div>
                        </td>
                        <td className="py-2 font-popinsMedium text-darkColor text-sm">
                          <span className="flex justify-center">
                            {item.status === "active" ? (
                              <SquareCheck color="#01CB78" size={20} />
                            ) : (
                              <SquareX color="#E43B3B" size={20} />
                            )}
                          </span>
                        </td>
                        {!showMap && (
                          <>
                            <td className="py-1 px-4 font-popinsMedium text-darkColor text-sm text-center">
                              {item?.unitCount}
                            </td>
                            <td className="py-1 px-4 font-popinsMedium text-darkColor text-sm text-center">
                              {item?.leaseUnitCount}
                            </td>
                            <td className="py-1 px-4 font-popinsMedium text-darkColor text-sm text-center">
                              {`${item.state}`}
                            </td>
                          </>
                        )}
                        <td className="py-1 px-4 font-popinsMedium text-darkColor text-sm text-center">
                          {`${item.category}`}
                        </td>
                        <td className="py-1 px-4 font-popinsMedium text-darkColor text-sm text-center">
                          {`${item.type}`}
                        </td>
                        <td className="pt-5 2xl:pt-4 px-4 flex justify-center items-center h-full">
                          <div
                            onClick={() => {
                              onViewDetails(item._id);
                            }}
                            className="flex justify-center items-center border border-themeColor w-7 h-7 rounded-md cursor-pointer"
                          >
                            <ArrowRight
                              color="#6F6F6F"
                              size={18}
                              strokeWidth={2}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {Loading && <LeaseTableLoader />}
              {!Loading &&
                filteredProperties.length === 0 &&
                AllProperty.length > 0 && (
                  <div className="w-full mt-5 text-center">
                    <p className="text-gray-500 font-popinsRegular">
                      No properties found matching your search.
                    </p>
                  </div>
                )}
              {AllProperty.length === 0 && !Loading && (
                <div className="w-full mt-5">
                  <NoData />
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex justify-center h-[10%]">
          <ReactPaginate
            previousLabel="Previous"
            nextLabel="Next"
            breakLabel="..."
            pageCount={Math.ceil(totalData / itemsPerPage)}
            marginPagesDisplayed={2}
            pageRangeDisplayed={3}
            onPageChange={handlePageClick}
            containerClassName="flex space-x-2 items-center"
            pageClassName="px-3 py-1 border rounded-md text-sm font-popinsMedium cursor-pointer"
            previousClassName="px-3 py-1 border rounded-l-md text-sm cursor-pointer font-popinsMedium"
            nextClassName="px-3 py-1 border rounded-r-md text-sm font-popinsMedium cursor-pointer font-popinsMedium"
            disabledClassName="opacity-50 !cursor-not-allowed"
            activeClassName="bg-themeColor text-white"
            breakClassName="px-3 py-1 text-sm font-popinsMedium"
          />
        </div>
      </div>{" "}
      :
      {showMap && (
        <div className="w-[35%] py-5">
          <MapSection />
        </div>
      )}
    </div>
  );
};

export default PropertyTable;
